/**
 * ServiceAgentVisibilityGuideModal
 *
 * Modal explicativo do switch "Visível para o agente" dos serviços
 * configurados em uma agenda interna.
 */

import { Modal, Button } from 'react-bootstrap';
import {
  IconBook2,
  IconRobot,
  IconCalendar,
  IconBulb,
  IconAlertTriangle,
  IconClock,
} from '@tabler/icons-react';
import '../../styles/ServiceAgentVisibilityGuideModal.css';

const ServiceAgentVisibilityGuideModal = ({ show, onHide }) => {
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="svg-modal"
      scrollable
    >
      <Modal.Header closeButton className="svg-modal__header">
        <Modal.Title className="svg-modal__title">
          <div className="svg-modal__title-icon">
            <IconBook2 size={22} />
          </div>
          Guia — Configuração do agente nesta agenda
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="svg-modal__body">
        <p className="svg-modal__intro">
          Cada serviço da agenda tem um interruptor com ícone de robô. Ele decide se o agente
          de IA pode oferecer aquele serviço durante a conversa — sem afetar o calendário em si.
        </p>

        <section className="svg-section">
          <h4 className="svg-section__title">
            <IconRobot size={16} /> O que o interruptor faz
          </h4>
          <div className="svg-states">
            <div className="svg-state svg-state--on">
              <div className="svg-state__chip">
                <span className="svg-state__toggle svg-state__toggle--on" />
                <IconRobot size={14} />
                <span>Visível</span>
              </div>
              <p className="svg-state__desc">
                <strong>Ligado</strong> (padrão). O agente conhece o serviço, oferece nas conversas
                e aceita agendamentos para ele.
              </p>
            </div>
            <div className="svg-state svg-state--off">
              <div className="svg-state__chip">
                <span className="svg-state__toggle svg-state__toggle--off" />
                <IconRobot size={14} style={{ color: '#94a3b8' }} />
                <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>Oculto</span>
              </div>
              <p className="svg-state__desc">
                <strong>Desligado.</strong> O agente não menciona nem aceita o serviço.
                Continua disponível no calendário para uso manual.
              </p>
            </div>
          </div>
        </section>

        <section className="svg-section">
          <h4 className="svg-section__title">
            <IconCalendar size={16} /> O que NÃO muda
          </h4>
          <ul className="svg-list">
            <li>
              O serviço continua aparecendo na <strong>agenda do calendário</strong> e pode ser
              agendado manualmente pelo atendente.
            </li>
            <li>
              Agendamentos antigos com esse serviço continuam válidos e visíveis.
            </li>
            <li>
              Os dados do serviço (nome, duração, preço) não são apagados — só o agente
              deixa de oferecê-lo.
            </li>
          </ul>
        </section>

        <section className="svg-section">
          <h4 className="svg-section__title">
            <IconBulb size={16} /> Quando usar
          </h4>
          <ul className="svg-list">
            <li>
              Serviços <strong>internos</strong> que aparecem na agenda mas não devem ser
              oferecidos ao cliente (ex.: bloqueio de horário, reunião interna).
            </li>
            <li>
              Serviços <strong>especiais</strong> que precisam de avaliação humana antes de
              agendar (ex.: orçamento sob medida).
            </li>
            <li>
              Serviços <strong>sazonais</strong> que você quer pausar temporariamente sem
              apagar — basta religar quando voltar.
            </li>
          </ul>
        </section>

        <div className="svg-callout svg-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Diferença para "Excluir":</strong> excluir remove o serviço de forma
            permanente — some também da agenda e do histórico. Desligar o interruptor apenas
            esconde do agente. Use o interruptor sempre que houver dúvida.
          </div>
        </div>

        <hr className="svg-divider" />

        <h3 className="svg-modal__subtitle">
          <IconClock size={18} /> Slot do agente nesta agenda
        </h3>
        <p className="svg-modal__intro">
          O bloco "Slot do agente nesta agenda" controla em que <strong>ritmo</strong> os
          horários são oferecidos ao cliente — independente do que aparece na grade do
          calendário (FullCalendar).
        </p>

        <section className="svg-section">
          <h4 className="svg-section__title">
            <IconRobot size={16} /> Dois modos
          </h4>
          <div className="svg-states">
            <div className="svg-state svg-state--on">
              <div className="svg-state__chip">
                <span className="svg-state__toggle svg-state__toggle--on" />
                <span>Slot fixo (ligado)</span>
              </div>
              <p className="svg-state__desc">
                <strong>Padrão.</strong> O agente sugere horários sempre no mesmo ritmo (ex:
                de 1h em 1h → 14h, 15h, 16h). Use o campo numérico para definir o intervalo
                desta agenda. Vazio = usa o padrão global da conta.
              </p>
            </div>
            <div className="svg-state svg-state--off">
              <div className="svg-state__chip">
                <span className="svg-state__toggle svg-state__toggle--off" />
                <span style={{ color: '#64748b' }}>Modo livre (desligado)</span>
              </div>
              <p className="svg-state__desc">
                Sem ritmo fixo. O agente alinha os horários à <strong>duração do serviço</strong>
                que o cliente escolher. Ideal quando os serviços têm durações muito diferentes
                (ex: corte de 30min, coloração de 2h).
              </p>
            </div>
          </div>
        </section>

        <section className="svg-section">
          <h4 className="svg-section__title">
            <IconBulb size={16} /> Como decidir
          </h4>
          <ul className="svg-list">
            <li>
              <strong>Slot fixo</strong> é o que a maioria dos negócios usa. Encaixa atendimentos
              em horários previsíveis (de hora em hora, de meia em meia).
            </li>
            <li>
              <strong>Modo livre</strong> é útil quando você não quer "buracos" entre serviços
              de durações diferentes — o agente passa a oferecer o próximo horário disponível
              conforme o serviço.
            </li>
          </ul>
        </section>

        <div className="svg-callout svg-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Nada disso afeta a grade visual do calendário (FullCalendar).</strong> Para
            mudar a grade visual, ajuste o slot global em <em>"Informações do Negócio →
            Configurações de Agendamento"</em>.
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="svg-modal__footer">
        <Button variant="primary" size="sm" onClick={onHide}>
          Entendi
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ServiceAgentVisibilityGuideModal;
